import { useState, useEffect, useCallback, useMemo } from 'react';
import { reaktivierungAPI, authAPI, settingsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  PhoneCall, Phone, Users, CalendarClock, TrendingUp, AlertTriangle, Award,
  Clock, RefreshCw, ChevronRight, CheckCircle2, XCircle, Loader2, Save,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Sprachregel: Anlass ist immer die Abwesenheit — nie der Beitrag.

const STUFE_LABEL = { aktiv: 'Aktiv', auffaellig: 'Auffällig', abwesend: 'Abwesend', kritisch: 'Kritisch', inaktiv: 'Inaktiv' };
const GRUENDE = [
  ['zeit', 'Zeit'], ['verletzung', 'Verletzung'], ['motivation', 'Motivation'],
  ['umzug', 'Umzug'], ['unzufrieden', 'Unzufrieden'], ['finanziell', 'Finanziell'], ['sonstiges', 'Sonstiges'],
];
const MASSNAHMEN = [
  ['pt_termin', 'PT-Termin buchen'], ['kommt_selbst', 'Kommt von selbst'],
  ['vertragspause', 'Vertragspause'], ['kuendigungsrisiko', 'Kündigungsrisiko'], ['keine', 'Keine'],
];
const TAGE = [['mo', 'Mo'], ['di', 'Di'], ['mi', 'Mi'], ['do', 'Do'], ['fr', 'Fr'], ['sa', 'Sa'], ['so', 'So']];
const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];

function fmtTag(dateStr) {
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return `${WOCHENTAGE[(d.getDay() + 6) % 7].slice(0, 2)}, ${d.getDate()}.${d.getMonth() + 1}.`;
  } catch { return dateStr; }
}

export default function ReaktivierungPage() {
  const { user, userPermissions, isAdminPermissions } = useAuth();
  const isAdmin = user?.role === 'admin';
  const darfAuswertung = isAdminPermissions || !!userPermissions?.['reaktivierung.auswertung'];

  const [tab, setTab] = useState('arbeitsliste');
  const [ueb, setUeb] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [uRes, tRes] = await Promise.all([reaktivierungAPI.uebersicht(), reaktivierungAPI.arbeitsliste()]);
      setUeb(uRes.data);
      setTasks(tRes.data.tasks || []);
    } catch { /* fehlende Rechte o. ä. */ }
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Gesprächsmaske
  const [dialogTask, setDialogTask] = useState(null);

  return (
    <div className="space-y-4 fade-in" data-testid="reaktivierung-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Reaktivierung</h1>
          <p className="text-muted-foreground text-sm">Wer länger nicht da war, bekommt einen Anruf — nicht eine Mahnung.</p>
        </div>
        {isAdmin && (
          <Button variant="outline" size="sm" className="gap-1.5 h-8 text-xs" data-testid="reakt-scan-btn"
            onClick={async () => { try { const r = await reaktivierungAPI.scan(); toast.success(`Scan: ${r.data.created ?? 0} neue Aufgaben`); loadAll(); } catch (e) { toast.error(e.response?.data?.detail || 'Scan fehlgeschlagen'); } }}>
            <RefreshCw className="w-3 h-3" /> Jetzt scannen
          </Button>
        )}
      </div>

      {/* 4 Kacheln */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <button onClick={() => setTab('arbeitsliste')} className="text-left" data-testid="kachel-rueckrufe">
          <Card className="border-border/60 hover:bg-muted/30 transition-colors h-full"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><PhoneCall className="w-3.5 h-3.5" />Offene Rückrufe</div>
            <div className="text-2xl font-bold mt-1">{ueb?.offene_rueckrufe ?? '–'}</div>
          </CardContent></Card>
        </button>
        <button onClick={() => setTab('abwesenheit')} className="text-left" data-testid="kachel-stufen">
          <Card className="border-border/60 hover:bg-muted/30 transition-colors h-full"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><Users className="w-3.5 h-3.5" />Abwesende nach Stufe</div>
            <div className="flex items-end gap-1.5 mt-2 h-8">
              {['auffaellig', 'abwesend', 'kritisch', 'inaktiv'].map(s => {
                const n = ueb?.stufen?.[s] ?? 0;
                const max = Math.max(1, ...['auffaellig', 'abwesend', 'kritisch', 'inaktiv'].map(x => ueb?.stufen?.[x] ?? 0));
                const farbe = { auffaellig: '#EAB308', abwesend: '#F97316', kritisch: '#EF4444', inaktiv: '#6B7280' }[s];
                return (
                  <div key={s} className="flex flex-col items-center flex-1">
                    <div className="w-full rounded-t" style={{ height: `${Math.max(8, n / max * 100)}%`, backgroundColor: farbe, minHeight: 4 }} />
                    <span className="text-[9px] text-muted-foreground mt-0.5">{n}</span>
                  </div>
                );
              })}
            </div>
          </CardContent></Card>
        </button>
        <button onClick={() => darfAuswertung && setTab('auswertung')} className="text-left" data-testid="kachel-quote">
          <Card className="border-border/60 hover:bg-muted/30 transition-colors h-full"><CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground"><TrendingUp className="w-3.5 h-3.5" />Reaktivierungsquote 30 T</div>
            <div className="text-2xl font-bold mt-1">{ueb?.quote_30_tage != null ? `${ueb.quote_30_tage}%` : '–'}</div>
            <div className="text-[10px] text-muted-foreground">{ueb?.quote_basis ?? 0} Gespräche als Basis</div>
          </CardContent></Card>
        </button>
        <Card className="border-border/60 h-full" data-testid="kachel-pt"><CardContent className="p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground"><CalendarClock className="w-3.5 h-3.5" />PT-Termine diese Woche</div>
          <div className="text-sm mt-1.5 space-y-0.5">
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Geplant</span><b>{ueb?.pt_woche?.geplant ?? 0}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">Wahrgenommen</span><b className="text-emerald-600">{ueb?.pt_woche?.wahrgenommen ?? 0}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground text-xs">No-show</span><b className="text-red-500">{ueb?.pt_woche?.no_show ?? 0}</b></div>
          </div>
        </CardContent></Card>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <div className="-mx-4 px-4 sm:mx-0 sm:px-0 overflow-x-auto">
          <TabsList className="inline-flex w-auto min-w-full sm:w-auto">
            <TabsTrigger value="arbeitsliste" className="text-xs px-3" data-testid="tab-arbeitsliste">Arbeitsliste</TabsTrigger>
            <TabsTrigger value="abwesenheit" className="text-xs px-3" data-testid="tab-abwesenheit">Abwesenheit</TabsTrigger>
            <TabsTrigger value="risiko" className="text-xs px-3" data-testid="tab-risiko">Kündigungsrisiko</TabsTrigger>
            {darfAuswertung && <TabsTrigger value="auswertung" className="text-xs px-3" data-testid="tab-auswertung">Auswertung</TabsTrigger>}
            {isAdmin && <TabsTrigger value="einstellungen" className="text-xs px-3" data-testid="tab-einstellungen">Einstellungen</TabsTrigger>}
          </TabsList>
        </div>

        <TabsContent value="arbeitsliste" className="mt-4">
          <Arbeitsliste tasks={tasks} loading={loading} onGespraech={(t) => setDialogTask(t)} />
        </TabsContent>
        <TabsContent value="abwesenheit" className="mt-4"><AbwesenheitTab /></TabsContent>
        <TabsContent value="risiko" className="mt-4"><RisikoTab /></TabsContent>
        {darfAuswertung && <TabsContent value="auswertung" className="mt-4"><AuswertungTab /></TabsContent>}
        {isAdmin && <TabsContent value="einstellungen" className="mt-4"><EinstellungenTab /></TabsContent>}
      </Tabs>

      <GespraechDialog task={dialogTask} onClose={(reload) => { setDialogTask(null); if (reload) loadAll(); }} />
    </div>
  );
}

/* ─── Arbeitsliste ─── */
function Arbeitsliste({ tasks, loading, onGespraech }) {
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!tasks.length) return (
    <Card className="border-border/60"><CardContent className="py-12 text-center text-muted-foreground">
      <CheckCircle2 className="w-10 h-10 mx-auto mb-2 opacity-20" />
      <p className="text-sm">Keine offenen Rückrufe — alles abtelefoniert. 💪</p>
    </CardContent></Card>
  );
  return (
    <Card className="border-border/60 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-muted/30 text-muted-foreground">
            <th className="text-left px-3 py-2.5 font-medium">Name</th>
            <th className="text-left px-3 py-2.5 font-medium hidden md:table-cell">Zielgruppe / Tarif</th>
            <th className="text-left px-3 py-2.5 font-medium">Letzter Besuch</th>
            <th className="text-left px-3 py-2.5 font-medium hidden lg:table-cell">Stammkurs</th>
            <th className="text-center px-3 py-2.5 font-medium hidden sm:table-cell">Gradierung</th>
            <th className="text-left px-3 py-2.5 font-medium">Telefon</th>
            <th className="px-3 py-2.5" />
          </tr></thead>
          <tbody>
            {tasks.map(t => (
              <tr key={t.id} className="border-b border-border/30 hover:bg-muted/20" data-testid={`task-${t.id}`}>
                <td className="px-3 py-2.5">
                  <div className="font-medium">{t.member_name}</div>
                  {t.status === 'in_arbeit' && <Badge variant="secondary" className="text-[9px] h-4 mt-0.5">In Arbeit{t.versuche > 0 ? ` · ${t.versuche}. Versuch` : ''}</Badge>}
                  {t.ergebnis === 'rueckruf_vereinbart' && <Badge variant="secondary" className="text-[9px] h-4 mt-0.5">Rückruf vereinbart</Badge>}
                </td>
                <td className="px-3 py-2.5 hidden md:table-cell text-xs text-muted-foreground">
                  {STUFE_LABEL[t.zielgruppe] || t.zielgruppe}{t.tarif ? ` · ${t.tarif}` : ''}
                </td>
                <td className="px-3 py-2.5">
                  <span className="font-semibold" style={{ color: t.stufe_farbe }}>vor {t.tage_seit_besuch} Tagen</span>
                  <div className="text-[10px] text-muted-foreground">{t.letzter_besuch_am}</div>
                </td>
                <td className="px-3 py-2.5 hidden lg:table-cell text-xs">{t.stammkurs || '—'}</td>
                <td className="px-3 py-2.5 text-center hidden sm:table-cell">
                  {t.gradierung_offen ? <Award className="w-4 h-4 text-amber-500 inline" /> : <span className="text-muted-foreground/40">—</span>}
                </td>
                <td className="px-3 py-2.5">
                  {t.phone ? (
                    <a href={`tel:${t.phone}`} className="flex items-center gap-1 text-primary text-xs font-medium hover:underline" data-testid={`tel-${t.id}`}>
                      <Phone className="w-3 h-3" />{t.phone}
                    </a>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </td>
                <td className="px-3 py-2.5 text-right">
                  <Button size="sm" className="h-7 text-xs" onClick={() => onGespraech(t)} data-testid={`gespraech-${t.id}`}>
                    Gespräch starten
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/* ─── Gesprächsmaske (Dialog) ─── */
function GespraechDialog({ task, onClose }) {
  const [ctx, setCtx] = useState(null);
  const [ergebnis, setErgebnis] = useState('');
  const [grund, setGrund] = useState('');
  const [massnahme, setMassnahme] = useState('');
  const [notiz, setNotiz] = useState('');
  const [busy, setBusy] = useState(false);
  const [slotMode, setSlotMode] = useState(false);
  const [slotData, setSlotData] = useState(null);
  const [slotFilter, setSlotFilter] = useState('');
  const [selSlot, setSelSlot] = useState(null);
  const [erfolg, setErfolg] = useState(null);

  useEffect(() => {
    if (!task) return;
    setCtx(null); setErgebnis(''); setGrund(''); setMassnahme(''); setNotiz('');
    setSlotMode(false); setSlotData(null); setSelSlot(null); setErfolg(null); setSlotFilter('');
    reaktivierungAPI.kontext(task.id).then(r => setCtx(r.data)).catch(() => {});
    reaktivierungAPI.start(task.id).catch(() => {});
  }, [task]);

  const speichern = async () => {
    if (!ergebnis) { toast.error('Bitte Ergebnis wählen'); return; }
    if (ergebnis === 'erreicht' && !grund) { toast.error('Abwesenheitsgrund ist Pflicht'); return; }
    setBusy(true);
    try {
      const res = await reaktivierungAPI.ergebnis(task.id, { ergebnis, abwesenheitsgrund: grund || null, massnahme: massnahme || null, notiz });
      if (massnahme === 'pt_termin' && ergebnis === 'erreicht') {
        // Weiter zum Slot-Picker
        setSlotMode(true);
        const s = await reaktivierungAPI.slots();
        setSlotData(s.data);
      } else {
        const st = res.data.task?.status;
        toast.success(st === 'abgebrochen' ? 'Nach 3 Versuchen abgebrochen — bitte schriftlich kontaktieren' : 'Gespräch gespeichert');
        onClose(true);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setBusy(false);
  };

  const buchen = async () => {
    if (!selSlot) { toast.error('Bitte Slot wählen'); return; }
    setBusy(true);
    try {
      const res = await reaktivierungAPI.termin(task.id, { trainer_id: selSlot.trainer_id, date: selSlot.date, start: selSlot.start });
      setErfolg(res.data);
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error('Slot gerade vergeben — Liste aktualisiert');
        const s = await reaktivierungAPI.slots(); setSlotData(s.data); setSelSlot(null);
      } else toast.error(e.response?.data?.detail || 'Buchung fehlgeschlagen');
    }
    setBusy(false);
  };

  const slotTage = useMemo(() => {
    if (!slotData?.days) return [];
    const inFilter = (s) => {
      const h = parseInt(s.start.split(':')[0], 10);
      if (slotFilter === 'vormittags') return h < 12;
      if (slotFilter === 'nachmittags') return h >= 12 && h < 17;
      if (slotFilter === 'abends') return h >= 17;
      return true;
    };
    return slotData.days
      .filter(d => slotFilter !== 'wochenende' || [0, 6].includes(new Date(d.date + 'T00:00:00').getDay()))
      .map(d => ({ ...d, slots: d.slots.filter(inFilter) }))
      .filter(d => d.slots.length);
  }, [slotData, slotFilter]);

  return (
    <Dialog open={!!task} onOpenChange={(v) => { if (!v) onClose(true); }}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="gespraech-dialog">
        <DialogHeader><DialogTitle>{erfolg ? 'Termin gebucht!' : slotMode ? 'Termin im Private-Bereich' : `Gespräch: ${ctx?.name || task?.member_name || ''}`}</DialogTitle></DialogHeader>

        {erfolg ? (
          <div className="text-center py-6 space-y-3" data-testid="gespraech-erfolg">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <p className="font-semibold">{fmtTag(erfolg.date)} · {erfolg.start}–{erfolg.end} Uhr</p>
            <p className="text-sm text-muted-foreground">bei {erfolg.trainer_name} · Bestätigung per E-Mail & App ist raus.</p>
            <Button onClick={() => onClose(true)} data-testid="gespraech-fertig">Fertig</Button>
          </div>
        ) : slotMode ? (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {[['', 'Alle'], ['vormittags', 'Vormittags'], ['nachmittags', 'Nachmittags'], ['abends', 'Abends'], ['wochenende', 'Wochenende']].map(([k, l]) => (
                <button key={k} onClick={() => setSlotFilter(k)} data-testid={`slotfilter-${k || 'alle'}`}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${slotFilter === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/40'}`}>
                  {l}
                </button>
              ))}
            </div>
            {!slotData ? <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
              : slotTage.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Keine freien Slots — Verfügbarkeit in den Einstellungen pflegen.</p>
              : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {slotTage.map(d => (
                    <div key={d.date}>
                      <p className="text-[11px] font-semibold text-muted-foreground mb-1">{fmtTag(d.date)}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {d.slots.map((s, i) => {
                          const sel = selSlot && selSlot.date === d.date && selSlot.start === s.start && selSlot.trainer_id === s.trainer_id;
                          return (
                            <button key={`${s.trainer_id}-${s.start}-${i}`} onClick={() => setSelSlot({ ...s, date: d.date })}
                              data-testid={`slot-${d.date}-${s.start}`}
                              className={`px-2.5 py-1.5 rounded-lg border-2 text-xs font-bold ${sel ? 'border-marke text-marke bg-marke/5' : 'border-border hover:border-border/80'}`}>
                              {s.start}{(slotData.trainer || []).length > 1 ? ` · ${s.trainer_name.split(' ')[0]}` : ''}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            <Button className="w-full" onClick={buchen} disabled={busy || !selSlot} data-testid="slot-buchen">
              {busy ? 'Wird gebucht…' : selSlot ? `${fmtTag(selSlot.date)} um ${selSlot.start} Uhr buchen` : 'Slot wählen'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Kontextblock */}
            <div className="bg-muted/40 rounded-lg p-3 grid grid-cols-2 gap-x-4 gap-y-1 text-xs" data-testid="gespraech-kontext">
              <div className="text-muted-foreground">Tarif</div><div className="font-medium">{ctx?.tarif || '—'}{ctx?.laufzeit_ende ? ` (bis ${ctx.laufzeit_ende})` : ''}</div>
              <div className="text-muted-foreground">Mitglied seit</div><div className="font-medium">{ctx?.mitglied_seit || '—'}</div>
              <div className="text-muted-foreground">Letzter Besuch</div><div className="font-medium">{ctx?.letzter_besuch_am || '—'}</div>
              <div className="text-muted-foreground">Stammkurs</div><div className="font-medium">{ctx?.stammkurs || '—'}</div>
              <div className="text-muted-foreground">Gradierung</div><div className="font-medium">{ctx?.gradierung_offen ? `offen (${ctx.gradierung_ziel})` : 'nein'}</div>
              {ctx?.letzte_notiz && (<><div className="text-muted-foreground">Letzte Notiz</div><div className="font-medium line-clamp-2">{ctx.letzte_notiz}</div></>)}
              {ctx?.phone && (<><div className="text-muted-foreground">Telefon</div><a href={`tel:${ctx.phone}`} className="font-medium text-primary hover:underline flex items-center gap-1"><Phone className="w-3 h-3" />{ctx.phone}</a></>)}
            </div>

            <div>
              <Label className="text-xs font-semibold">Ergebnis *</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[['erreicht', 'Erreicht', CheckCircle2], ['nicht_erreicht', 'Nicht erreicht', XCircle], ['rueckruf_vereinbart', 'Rückruf vereinbart', Clock]].map(([k, l, Icon]) => (
                  <button key={k} onClick={() => setErgebnis(k)} data-testid={`ergebnis-${k}`}
                    className={`flex items-center justify-center gap-1.5 py-2.5 rounded-xl border-2 text-xs font-bold ${ergebnis === k ? 'border-marke text-marke bg-marke/5' : 'border-border text-muted-foreground hover:border-border/80'}`}>
                    <Icon className="w-3.5 h-3.5" />{l}
                  </button>
                ))}
              </div>
              {ergebnis === 'nicht_erreicht' && <p className="text-[10px] text-muted-foreground mt-1">Aufgabe bleibt offen · nach 3 Versuchen automatisch „schriftlich kontaktieren"</p>}
            </div>

            {ergebnis === 'erreicht' && (
              <div>
                <Label className="text-xs font-semibold">Abwesenheitsgrund *</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {GRUENDE.map(([k, l]) => (
                    <button key={k} onClick={() => setGrund(k)} data-testid={`grund-${k}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${grund === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/40'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ergebnis === 'erreicht' && (
              <div>
                <Label className="text-xs font-semibold">Maßnahme</Label>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {MASSNAHMEN.map(([k, l]) => (
                    <button key={k} onClick={() => setMassnahme(k)} data-testid={`massnahme-${k}`}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium border ${massnahme === k ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted/40'}`}>
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs font-semibold">Notiz</Label>
              <textarea value={notiz} onChange={e => setNotiz(e.target.value)} rows={2} data-testid="gespraech-notiz"
                className="w-full mt-1 px-3 py-2 border border-border rounded-lg text-sm resize-none bg-background" placeholder="Kurznotiz zum Gespräch…" />
            </div>

            <Button className="w-full" onClick={speichern} disabled={busy} data-testid="gespraech-speichern">
              {busy ? 'Speichert…' : massnahme === 'pt_termin' && ergebnis === 'erreicht' ? 'Weiter: Termin wählen' : 'Gespräch speichern'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ─── Abwesenheit (alle aktiven Mitglieder mit Stufe) ─── */
function AbwesenheitTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    reaktivierungAPI.abwesenheit().then(r => setRows(r.data.mitglieder || [])).finally(() => setLoading(false));
  }, []);
  const counts = useMemo(() => rows.reduce((a, r) => { a[r.stufe] = (a[r.stufe] || 0) + 1; return a; }, {}), [rows]);
  const shown = filter ? rows.filter(r => r.stufe === filter) : rows;
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        <button onClick={() => setFilter('')} className={`px-2.5 py-1 rounded-full text-xs font-medium border ${!filter ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>Alle ({rows.length})</button>
        {Object.keys(STUFE_LABEL).map(s => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`stufe-filter-${s}`}
            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${filter === s ? 'bg-primary text-primary-foreground border-primary' : 'border-border'}`}>
            {STUFE_LABEL[s]} ({counts[s] || 0})
          </button>
        ))}
      </div>
      <Card className="border-border/60 overflow-hidden">
        <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted/30 text-muted-foreground sticky top-0">
              <th className="text-left px-3 py-2 font-medium">Name</th>
              <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Tarif</th>
              <th className="text-left px-3 py-2 font-medium">Letzter Besuch</th>
              <th className="text-left px-3 py-2 font-medium">Stufe</th>
              <th className="text-left px-3 py-2 font-medium hidden sm:table-cell">Telefon</th>
            </tr></thead>
            <tbody>
              {shown.map(r => (
                <tr key={r.member_id} className="border-b border-border/30">
                  <td className="px-3 py-2 font-medium">{r.name}</td>
                  <td className="px-3 py-2 hidden sm:table-cell text-xs text-muted-foreground">{r.tarif}</td>
                  <td className="px-3 py-2 text-xs">{r.letzter_besuch_am ? `${r.letzter_besuch_am} (vor ${r.tage_seit_besuch} T.)` : 'kein Besuch erfasst'}</td>
                  <td className="px-3 py-2"><span className="px-2 py-0.5 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: r.stufe_farbe }}>{STUFE_LABEL[r.stufe]}</span></td>
                  <td className="px-3 py-2 hidden sm:table-cell">{r.phone ? <a href={`tel:${r.phone}`} className="text-primary text-xs hover:underline">{r.phone}</a> : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

/* ─── Kündigungsrisiko ─── */
function RisikoTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { reaktivierungAPI.kuendigungsrisiko().then(r => setLeads(r.data.leads || [])).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  if (!leads.length) return <Card className="border-border/60"><CardContent className="py-10 text-center text-sm text-muted-foreground">Keine Mitglieder als Kündigungsrisiko markiert.</CardContent></Card>;
  return (
    <div className="space-y-2">
      {leads.map(l => (
        <Card key={l.id} className="border-red-200 dark:border-red-900/40" data-testid={`risiko-${l.id}`}>
          <CardContent className="p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{l.name}</p>
              {l.notes && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{l.notes}</p>}
            </div>
            {l.phone && <a href={`tel:${l.phone}`} className="text-primary text-xs font-medium hover:underline flex items-center gap-1 flex-shrink-0"><Phone className="w-3 h-3" />{l.phone}</a>}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

/* ─── Auswertung ─── */
function AuswertungTab() {
  const [data, setData] = useState(null);
  useEffect(() => { reaktivierungAPI.auswertung().then(r => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;
  const gruende = Object.entries(data.gruende || {}).sort((a, b) => b[1] - a[1]);
  const maxG = Math.max(1, ...gruende.map(([, n]) => n));
  const GRUND_LABEL = Object.fromEntries(GRUENDE);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card className="border-border/60"><CardHeader className="pb-2"><CardTitle className="text-sm">Abwesenheitsgründe</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {gruende.length === 0 ? <p className="text-xs text-muted-foreground">Noch keine Gespräche mit erfasstem Grund.</p> :
            gruende.map(([g, n]) => (
              <div key={g} className="flex items-center gap-2 text-xs">
                <span className="w-24 text-muted-foreground">{GRUND_LABEL[g] || g}</span>
                <div className="flex-1 bg-muted rounded-full h-3 overflow-hidden"><div className="h-full bg-marke" style={{ width: `${n / maxG * 100}%` }} /></div>
                <b className="w-6 text-right">{n}</b>
              </div>
            ))}
          <p className="text-[10px] text-muted-foreground pt-1">Die wertvollste Zahl im Modul: nach 3 Monaten weißt du, WARUM Leute wegbleiben.</p>
        </CardContent>
      </Card>

      <Card className="border-border/60"><CardHeader className="pb-2"><CardTitle className="text-sm">Angerufen vs. Kontrollgruppe (Wiederkehr in 30 T.)</CardTitle></CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-muted-foreground text-xs">Angerufen</span>
            <b>{data.vergleich?.angerufen?.quote != null ? `${data.vergleich.angerufen.quote}%` : '–'} <span className="text-[10px] text-muted-foreground font-normal">({data.vergleich?.angerufen?.n ?? 0} Aufgaben)</span></b></div>
          <div className="flex justify-between"><span className="text-muted-foreground text-xs">Kontrollgruppe (nicht angerufen)</span>
            <b>{data.vergleich?.kontrollgruppe?.quote != null ? `${data.vergleich.kontrollgruppe.quote}%` : '–'} <span className="text-[10px] text-muted-foreground font-normal">({data.vergleich?.kontrollgruppe?.n ?? 0})</span></b></div>
          <p className="text-[10px] text-muted-foreground pt-1">Die einzige Zahl, die beweist, ob das Modul wirkt.</p>
        </CardContent>
      </Card>

      <Card className="border-border/60"><CardHeader className="pb-2"><CardTitle className="text-sm">Reaktivierungsquote je Mitarbeiter/in</CardTitle></CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          {(data.quote_je_owner || []).length === 0 ? <p className="text-xs text-muted-foreground">Noch keine erledigten Gespräche.</p> :
            data.quote_je_owner.map(o => (
              <div key={o.owner_id} className="flex justify-between text-xs">
                <span>{o.name}</span><b>{o.quote}% <span className="text-muted-foreground font-normal">({o.reaktiviert}/{o.gespraeche})</span></b>
              </div>
            ))}
        </CardContent>
      </Card>

      <Card className="border-border/60"><CardHeader className="pb-2"><CardTitle className="text-sm">Gebuchte PT-Termine</CardTitle></CardHeader>
        <CardContent className="space-y-1.5 text-sm">
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Gesamt</span><b>{data.termine?.gesamt ?? 0}</b></div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">Wahrgenommen</span><b className="text-emerald-600">{data.termine?.wahrgenommen ?? 0}</b></div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">No-show</span><b className="text-red-500">{data.termine?.no_show ?? 0}</b></div>
          <div className="flex justify-between text-xs"><span className="text-muted-foreground">No-show-Quote</span><b>{data.termine?.no_show_quote != null ? `${data.termine.no_show_quote}%` : '–'}</b></div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ─── Einstellungen (nur Admin; globaler settings.reaktivierung-Block) ─── */
const REAKT_DEFAULT = {
  aktiv: false, owner_user_id: '', schwellen: { kind: 14, jugend: 14, erwachsen: 21 },
  cooldown_tage: 30, max_versuche: 3, kontrollgruppe_prozent: 10,
  dauer_minuten: 30, puffer_minuten: 5, vorlauf_stunden: 24, vorschau_tage: 14, trainer: [],
};

function EinstellungenTab() {
  const [cfg, setCfg] = useState(null);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    settingsAPI.get().then(r => setCfg({ ...REAKT_DEFAULT, ...(r.data.reaktivierung || {}) }));
    authAPI.users().then(r => setStaff(r.data || [])).catch(() => {});
  }, []);
  if (!cfg) return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" /></div>;

  const patch = (upd) => setCfg(p => ({ ...p, ...upd }));
  const save = async () => {
    setSaving(true);
    try { await settingsAPI.update({ reaktivierung: cfg }); toast.success('Einstellungen gespeichert'); }
    catch (e) { toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    setSaving(false);
  };

  const addTrainer = () => patch({ trainer: [...(cfg.trainer || []), { trainer_id: '', aktiv: true, verfuegbarkeit: { mo: [], di: [], mi: [], do: [], fr: [], sa: [], so: [] } }] });
  const setTrainer = (i, upd) => { const t = [...cfg.trainer]; t[i] = { ...t[i], ...upd }; patch({ trainer: t }); };
  const setTrainerTag = (i, key, windows) => { const t = [...cfg.trainer]; t[i] = { ...t[i], verfuegbarkeit: { ...(t[i].verfuegbarkeit || {}), [key]: windows } }; patch({ trainer: t }); };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="border-border/60"><CardHeader className="pb-3"><CardTitle className="text-sm">Erkennung & Aufgaben</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
            <div><p className="text-sm font-medium">Modul aktiv</p><p className="text-xs text-muted-foreground">Nächtlicher Scan (03:00 Uhr) erzeugt Rückruf-Aufgaben</p></div>
            <Switch checked={!!cfg.aktiv} onCheckedChange={v => patch({ aktiv: v })} data-testid="reakt-aktiv" />
          </div>
          <div>
            <Label className="text-xs">Standard-Besitzerin der Aufgaben</Label>
            <select value={cfg.owner_user_id || ''} onChange={e => patch({ owner_user_id: e.target.value })} data-testid="reakt-owner"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
              <option value="">— bitte wählen —</option>
              {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Schwelle Kinder (Tage)</Label><Input type="number" min="3" value={cfg.schwellen?.kind ?? 14} onChange={e => patch({ schwellen: { ...cfg.schwellen, kind: parseInt(e.target.value) || 14 } })} /></div>
            <div><Label className="text-xs">Schwelle Jugend</Label><Input type="number" min="3" value={cfg.schwellen?.jugend ?? 14} onChange={e => patch({ schwellen: { ...cfg.schwellen, jugend: parseInt(e.target.value) || 14 } })} /></div>
            <div><Label className="text-xs">Schwelle Erwachsene</Label><Input type="number" min="3" value={cfg.schwellen?.erwachsen ?? 21} onChange={e => patch({ schwellen: { ...cfg.schwellen, erwachsen: parseInt(e.target.value) || 21 } })} /></div>
            <div><Label className="text-xs">Cooldown (Tage)</Label><Input type="number" min="7" value={cfg.cooldown_tage} onChange={e => patch({ cooldown_tage: parseInt(e.target.value) || 30 })} /></div>
            <div><Label className="text-xs">Max. Anrufversuche</Label><Input type="number" min="1" max="10" value={cfg.max_versuche} onChange={e => patch({ max_versuche: parseInt(e.target.value) || 3 })} /></div>
            <div><Label className="text-xs">Kontrollgruppe (%)</Label><Input type="number" min="0" max="50" value={cfg.kontrollgruppe_prozent} onChange={e => patch({ kontrollgruppe_prozent: parseInt(e.target.value) || 0 })} /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60"><CardHeader className="pb-3"><CardTitle className="text-sm">PT-Termine (Private-Bereich)</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div><Label className="text-xs">Dauer (Min.)</Label><Input type="number" min="15" value={cfg.dauer_minuten} onChange={e => patch({ dauer_minuten: parseInt(e.target.value) || 30 })} /></div>
            <div><Label className="text-xs">Puffer (Min.)</Label><Input type="number" min="0" value={cfg.puffer_minuten} onChange={e => patch({ puffer_minuten: parseInt(e.target.value) || 0 })} /></div>
            <div><Label className="text-xs">Vorlauf (Std.)</Label><Input type="number" min="0" value={cfg.vorlauf_stunden} onChange={e => patch({ vorlauf_stunden: parseInt(e.target.value) || 0 })} /></div>
            <div><Label className="text-xs">Vorschau (Tage)</Label><Input type="number" min="1" max="60" value={cfg.vorschau_tage} onChange={e => patch({ vorschau_tage: parseInt(e.target.value) || 14 })} /></div>
          </div>

          <div className="space-y-3">
            {(cfg.trainer || []).map((t, i) => (
              <div key={i} className="border border-border/60 rounded-lg p-3 space-y-2" data-testid={`trainer-${i}`}>
                <div className="flex items-center gap-2">
                  <select value={t.trainer_id || ''} onChange={e => setTrainer(i, { trainer_id: e.target.value })}
                    className="flex h-8 flex-1 rounded-md border border-input bg-background px-2 text-xs shadow-sm">
                    <option value="">— Trainer wählen —</option>
                    {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                  </select>
                  <label className="flex items-center gap-1.5 text-xs"><Switch checked={!!t.aktiv} onCheckedChange={v => setTrainer(i, { aktiv: v })} />aktiv</label>
                  <button onClick={() => patch({ trainer: cfg.trainer.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive text-xs">✕</button>
                </div>
                <div className="space-y-1">
                  {TAGE.map(([key, label]) => {
                    const win = (t.verfuegbarkeit?.[key] || [])[0];
                    return (
                      <div key={key} className="flex items-center gap-2 text-xs">
                        <label className="flex items-center gap-1.5 w-14 cursor-pointer">
                          <input type="checkbox" checked={!!win} onChange={e => setTrainerTag(i, key, e.target.checked ? [['17:00', '21:00']] : [])} className="w-3.5 h-3.5 accent-marke" />
                          {label}
                        </label>
                        {win && (<>
                          <Input type="time" value={win[0]} className="w-24 h-7 text-xs" onChange={e => setTrainerTag(i, key, [[e.target.value, win[1]]])} />
                          <span className="text-muted-foreground">–</span>
                          <Input type="time" value={win[1]} className="w-24 h-7 text-xs" onChange={e => setTrainerTag(i, key, [[win[0], e.target.value]])} />
                        </>)}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={addTrainer} className="h-8 text-xs" data-testid="trainer-add">+ Trainer hinzufügen</Button>
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} className="gap-2" data-testid="reakt-save"><Save className="w-4 h-4" />{saving ? 'Speichert…' : 'Speichern'}</Button>
    </div>
  );
}
